import * as React from "react";
import {
  ArrowBack,
  ArrowForward,
  ThumbDown,
  ThumbUp
} from "@material-ui/icons";
import { Container, Fab, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { makeStyles } from "@mui/styles";
import {
  markOrderAsArtworkChangeRequestedMutation,
  markOrderAsArtworkReviewedMutation,
  markOrderAsProofCompletedMutation
} from "../../../../../graph";
import { query } from "../../../ArtReviewApp/components/ArtReviewOrderDetails/graph";
import { useMutation, useQuery } from "@apollo/client";
import { useRouter } from "found";
import { useState } from "react";
import Checkbox from "@mui/material/Checkbox";
import Divider from "@mui/material/Divider";
import FailReasonDialog from "../FailReasonDialog";
import FormControlLabel from "@mui/material/FormControlLabel";
import PendingStatusView from "../../../../../components/PendingStatusView";
import buildArtworkChangeRequestDescription from "./artworkChangeRequestDescription";
import withSnackbar from "../../../../../components/withSnackbar";

const useStyles = makeStyles(theme => ({
  actionButtonsContainer: {
    marginTop: theme.spacing(2)
  },

  absoluteWrapper: {
    position: "absolute",
    top: 48,
    bottom: 0,
    left: 0,
    width: "100%",
    display: "flex",
    flexDirection: "column"
  },

  content: {
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    margin: "25px"
  },

  spinnerWrapper: {
    padding: theme.spacing(1),
    position: "relative",
    textAlign: "center"
  },

  missingArtworkLabel: {
    color: "red"
  },

  notesContainer: {
    marginLeft: "30px"
  },

  customizationAreaDivider: {
    marginTop: "15px"
  },

  artworkUrl: {
    color: theme.palette.primary.main,
    textDecoration: "none",
    "&:hover": {
      color: theme.palette.primary.dark
    }
  },

  templateCaption: {
    color: theme.palette.primary.dark,
    margin: "0"
  },

  customizationAreaDetail: {
    margin: "0"
  },

  divider: {
    marginTop: "10px"
  },

  previewImage: {
    maxWidth: "100%",
    maxHeight: "100%"
  },

  notesList: {
    marginTop: "0"
  },

  customizationAreaContainer: {
    display: "flex",
    gap: "10px"
  },

  customizationAreaImageItem: {
    flex: 1
  },

  nextButton: {
    marginLeft: "20px"
  },

  reviewingActionButton: {
    marginTop: "20px",
    marginBottom: "20px"
  },

  passButton: {
    marginLeft: "20px",
    marginRight: "20px"
  },

  infoIcon: {
    marginRight: "5px"
  },

  summaryDivider: {
    marginTop: "20px",
    marginBottom: "20px"
  },

  zoomLink: {
    color: theme.palette.primary.main,
    textDecoration: "none",
    "&:hover": {
      color: theme.palette.primary.dark
    }
  }
}));

const ArtReviewOrderDetails = ({
  onComplete,
  orderId,
  showErrorSnackbar,
  showSuccessSnackbar
}) => {
  const classes = useStyles();

  const { router } = useRouter();

  const { data, loading, error } = useQuery(query, {
    skip: !orderId,
    variables: { orderId }
  });

  // Mutations:
  const [markOrderAsArtworkChangeRequested] = useMutation(
    markOrderAsArtworkChangeRequestedMutation
  );
  const [markOrderAsArtworkReviewed] = useMutation(
    markOrderAsArtworkReviewedMutation
  );
  const [markOrderAsProofCompleted] = useMutation(
    markOrderAsProofCompletedMutation
  );

  const [passing, setPassing] = useState(true);
  const [currentState, setCurrentState] = useState("PREVIEW");
  const [customizationAreaIndex, setCustomizationAreaIndex] = useState(0);
  const [failDialogIsOpen, setFailDialogIsOpen] = useState(false);
  const [failedCustomizationAreas, setFailedCustomizationAreas] = useState([]);

  if (loading) return <PendingStatusView status="Loading..." />;

  if (error) return null;

  const artNotesCollection = data?.order?.artNotesCollection;
  const productionJobs = data?.order?.productionJobs || [];
  const orderNumber = data?.order?.orderNumber;
  const customizationAreas = productionJobs
    .flatMap(({ customizationAreas }) => customizationAreas)
    .filter(({ gangSheet }) => !gangSheet);
  const currentCustomizationArea = customizationAreas[customizationAreaIndex];
  const customizationAreaId = currentCustomizationArea
    ? Buffer.from(currentCustomizationArea.id, "base64")
        .toString("utf8")
        .split(":")[1]
    : null;

  const handleReviewArtworkButtonClick = () => {
    markOrderAsProofCompleted({ variables: { orderId } })
      .then(
        ({
          data: {
            markOrderAsProofCompleted: { succeeded, errors }
          }
        }) => {
          if (succeeded) {
            markOrderAsArtworkReviewed({ variables: { orderId } }).then(
              ({
                data: {
                  markOrderAsArtworkReviewed: { succeeded, errors }
                }
              }) => {
                if (succeeded) {
                  showSuccessSnackbar(`The order has passed art review.`);
                  router.push("/apps/art-review");
                } else {
                  showErrorSnackbar(
                    `Something went wrong: ${errors.orderId.join(", ")}`
                  );
                }
              }
            );
          } else {
            showErrorSnackbar(
              `Something went wrong: ${errors.orderId.join(", ")}`
            );
          }
        }
      )
      .catch(e => {
        showErrorSnackbar(`Something went wrong: ${e.message}`);
      })
      .finally(() => {
        if (onComplete) {
          onComplete();
        }
      });
  };

  const handleStartReviewingClick = () => {
    setCurrentState("REVIEWING");
    setCustomizationAreaIndex(0);
  };

  const handlePreviousClick = () => {
    setCustomizationAreaIndex(customizationAreaIndex - 1);
  };

  const handleNextClick = () => {
    if (customizationAreaIndex < customizationAreas.length - 1) {
      setCustomizationAreaIndex(customizationAreaIndex + 1);
    } else {
      setCurrentState("SUMMARY");
    }
  };

  const handlePassClick = () => {
    window.analytics.track("Customization Area Reviewed", {
      orderId: orderNumber,
      customizationAreaId: customizationAreaId,
      result: "pass",
      extension:
        currentCustomizationArea.customerArtwork.length > 0
          ? currentCustomizationArea.customerArtwork[0].file.asset.extension
          : null
    });

    handleNextClick();
  };

  const handleFailClick = () => {
    setFailDialogIsOpen(true);
  };

  const handleBackToSummaryClick = () => {
    setCurrentState("PREVIEW");
    setCustomizationAreaIndex(0);
    setFailedCustomizationAreas([]);
  };

  const handleArtworkReviewFailedButtonClick = () => {
    markOrderAsArtworkChangeRequested({
      variables: {
        orderId,
        description: buildArtworkChangeRequestDescription(
          failedCustomizationAreas
        )
      }
    })
      .then(
        ({
          data: {
            markOrderAsArtworkChangeRequested: { succeeded, errors }
          }
        }) => {
          if (succeeded) {
            setFailedCustomizationAreas([]);
            showSuccessSnackbar(`The order has failed art review.`);
            router.push("/apps/art-review");
          } else {
            showErrorSnackbar(
              `Something went wrong: ${errors.orderId.join(", ")}`
            );
          }
        }
      )
      .catch(e => {
        showErrorSnackbar(`Something went wrong: ${e.message}`);
      })
      .finally(() => {
        if (onComplete) {
          onComplete();
        }
      });
  };

  const handleCompleteReviewClick = () => {
    if (passing) {
      setCurrentState("PREVIEW");
      setCustomizationAreaIndex(0);
      handleReviewArtworkButtonClick();
    } else {
      setCurrentState("PREVIEW");
      setCustomizationAreaIndex(0);
      setPassing(true);
      handleArtworkReviewFailedButtonClick();
    }
  };

  if (currentState === "PREVIEW") {
    return (
      <Container maxWidth="lg">
        <SummaryView
          artNotesCollection={artNotesCollection}
          classes={classes}
          customizationAreas={customizationAreas}
          orderNumber={orderNumber}
        />
        <Fab
          color="primary"
          variant="extended"
          onClick={handleStartReviewingClick}
        >
          Start Reviewing
        </Fab>
      </Container>
    );
  }

  if (currentState === "REVIEWING") {
    return (
      <Container maxWidth="lg">
        <CustomizationAreaDetailView
          artNotesCollection={artNotesCollection}
          customizationArea={customizationAreas[customizationAreaIndex]}
          classes={classes}
          index={customizationAreaIndex}
        />
        <FailReasonDialog
          isOpen={failDialogIsOpen}
          onDialogClose={() => {
            setFailDialogIsOpen(false);
          }}
          onDialogSubmit={result => {
            const { reason, details } = result;
            const failedCustomizationArea = {
              id: customizationAreas[customizationAreaIndex].id,
              reason,
              details
            };

            setFailDialogIsOpen(false);
            setPassing(false);
            setFailedCustomizationAreas(currentFailedCustomizationAreas => [
              ...currentFailedCustomizationAreas.filter(
                ({ id }) => id !== failedCustomizationArea.id
              ),
              failedCustomizationArea
            ]);

            window.analytics.track("Customization Area Reviewed", {
              additionalInformation: details,
              customizationAreaId: customizationAreaId,
              orderId: orderNumber,
              reason: reason || "other reason",
              result: "fail",
              extension:
                customizationAreas[customizationAreaIndex].customerArtwork
                  .length > 0
                  ? customizationAreas[customizationAreaIndex]
                      .customerArtwork[0].file.asset.extension
                  : null
            });

            handleNextClick();
          }}
        />
        <div>
          <FormControlLabel
            control={
              <Checkbox
                checked={
                  customizationAreas[customizationAreaIndex].removeBackground
                }
                color="primary"
              />
            }
            label="Remove Background"
          />
        </div>
        <a href="#" onClick={handleBackToSummaryClick}>
          Back to summary
        </a>
        <Container
          maxWidth="lg"
          style={{
            bottom: 0,
            overflow: "hidden",
            padding: "10px",
            position: "fixed",
            width: "100%"
          }}
        >
          <div>
            <div
              style={{
                display: "flex",
                flexDirection: "row"
              }}
            >
              <div style={{ width: "50%" }}>
                <Fab
                  color="primary"
                  variant="extended"
                  style={{
                    marginRight: "10px"
                  }}
                  onClick={handlePreviousClick}
                  disabled={customizationAreaIndex === 0}
                >
                  <ArrowBack /> Previous
                </Fab>
                <Fab
                  color="primary"
                  variant="extended"
                  onClick={handleNextClick}
                >
                  <ArrowForward /> Next
                </Fab>
              </div>
              <div style={{ width: "50%", textAlign: "right" }}>
                <Fab
                  color="primary"
                  style={{
                    marginLeft: "10px",
                    marginRight: "10px"
                  }}
                  variant="extended"
                  onClick={handlePassClick}
                >
                  <ThumbUp /> Pass
                </Fab>
                <Fab
                  color="primary"
                  variant="extended"
                  onClick={handleFailClick}
                >
                  <ThumbDown /> Fail
                </Fab>
              </div>
            </div>
          </div>
        </Container>
      </Container>
    );
  }

  if (currentState === "SUMMARY") {
    return (
      <Container maxWidth="lg">
        <SummaryView
          artNotesCollection={artNotesCollection}
          classes={classes}
          customizationAreas={customizationAreas}
          orderNumber={orderNumber}
        />
        <Fab
          color="primary"
          variant="extended"
          onClick={handleCompleteReviewClick}
        >
          Complete Review - {passing ? "Artwork Uploaded" : "Needs Artwork"}
        </Fab>
      </Container>
    );
  }

  return null;
};

const SummaryDetailView = ({ customizationArea, index, classes }) => {
  const { method, gangsheet, width, height, quantity } = customizationArea;
  const product =
    method === "TRANSFER_STANDARD"
      ? "DTF Standard Transfers"
      : method === "TRANSFER_GLITTER"
      ? "DTF Glitter Transfers"
      : method === "TRANSFER_UV"
      ? "UV DTF 3D PermaStickers™"
      : null;
  const label = `${product} ${
    gangsheet ? "Gangsheet" : "by Size"
  } ${width}" x ${height}"`;

  const customerArtwork = customizationArea.customerArtwork[0];
  const artwork = customizationArea.productionArtwork[0];

  const customerArtworkUrl = customerArtwork?.file?.asset?.url?.formatted;
  const artworkUrl = artwork?.remoteAsset?.url?.formatted;

  return (
    <>
      <div style={{ display: "flex", flexDirection: "row" }}>
        <div className={classes.customizationAreaContainer}>
          <div className={classes.customizationAreaImageItem}>
            {customerArtwork ? (
              <img
                style={{
                  maxWidth: "100px",
                  backgroundImage:
                    "linear-gradient(45deg, #b1b1b1 25%, transparent 25%), linear-gradient(-45deg, #b1b1b1 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #b1b1b1 75%), linear-gradient(-45deg, transparent 75%, #b1b1b1 75%)",
                  backgroundSize: "20px 20px",
                  backgroundPosition: "0 0, 0 10px, 10px -10px, -10px 0px"
                }}
                src={customerArtworkUrl}
                alt="customerArtworkUrl"
              />
            ) : (
              <Typography className={classes.missingArtworkLabel}>
                Missing artwork
              </Typography>
            )}
          </div>
          <div className={classes.customizationAreaImageItem}>
            {artworkUrl ? (
              <img
                style={{
                  maxWidth: "100px",
                  backgroundImage:
                    "linear-gradient(45deg, #b1b1b1 25%, transparent 25%), linear-gradient(-45deg, #b1b1b1 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #b1b1b1 75%), linear-gradient(-45deg, transparent 75%, #b1b1b1 75%)",
                  backgroundSize: "20px 20px",
                  backgroundPosition: "0 0, 0 10px, 10px -10px, -10px 0px"
                }}
                src={artworkUrl}
                alt="artworkUrl"
              />
            ) : (
              <Typography className={classes.missingArtworkLabel}>
                Missing artwork
              </Typography>
            )}
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", flexGrow: 6 }}>
          <Typography>{label}</Typography>
          <Typography variant="subtitle1">
            Line Item: {index}, Quantity: {quantity}
          </Typography>
        </div>
      </div>
      <Divider className={classes.summaryDivider} />
    </>
  );
};

const OrderNotesView = ({ artNotesCollection }) => {
  if (artNotesCollection && artNotesCollection.length > 0) {
    return (
      <div
        style={{
          background: "yellow",
          padding: "10px",
          marginBottom: "10px",
          borderRadius: "15px"
        }}
      >
        <Typography variant="h6">Order Artwork Notes:</Typography>
        <ul>
          {artNotesCollection.map((item, index) => {
            if (item.__typename === "FormattedNote") {
              const formattedNote = `${item.author} (${DateTime.fromISO(
                item.createdAt,
                {
                  setZone: true
                }
              ).toLocaleString(DateTime.DATETIME_SHORT)}): ${item.note}`;
              return <li key={index}>{formattedNote}</li>;
            }

            return <li key={index}>{item.note}</li>;
          })}
        </ul>
      </div>
    );
  }

  return null;
};

const SummaryView = ({
  classes,
  customizationAreas,
  orderNumber,
  artNotesCollection
}) => (
  <>
    <Typography variant="h6" className={classes.templateCaption}>
      {orderNumber} - Review Summary
    </Typography>
    {customizationAreas.map((customizationArea, index) => (
      <SummaryDetailView
        key={customizationArea.id}
        customizationArea={customizationArea}
        index={index + 1}
        classes={classes}
      />
    ))}
    <OrderNotesView artNotesCollection={artNotesCollection} />
  </>
);

const CustomizationAreaDetailView = ({
  artNotesCollection,
  classes,
  customizationArea,
  index
}) => {
  const { method, gangsheet, width, height, quantity } = customizationArea;
  const product =
    method === "TRANSFER_STANDARD"
      ? "DTF Standard Transfers"
      : method === "TRANSFER_GLITTER"
      ? "DTF Glitter Transfers"
      : method === "TRANSFER_UV"
      ? "UV DTF 3D PermaStickers™"
      : null;
  const label = `${product} ${
    gangsheet ? "Gangsheet" : "by Size"
  } ${width}" x ${height}"`;

  const notes = customizationArea.notesCollection;

  const customerArtwork = customizationArea.customerArtwork[0];
  const artwork = customizationArea.productionArtwork[0];

  const customerArtworkUrl = customerArtwork?.file?.asset?.url?.formatted;
  const artworkUrl = artwork?.remoteAsset?.url?.formatted;

  return (
    <div>
      <Typography className={classes.templateCaption} variant="h6">
        {label}
      </Typography>
      {notes.length > 0 && (
        <div className={classes.notesContainer}>
          <Typography className={classes.customizationAreaDetail}>
            Notes:
          </Typography>
          <ul className={classes.notesList}>
            {notes.map((note, index) => (
              <li key={index}>
                {note.author}: {note.note}
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className={classes.divider} />
      <div className={classes.customizationAreaContainer}>
        <div className={classes.customizationAreaImageItem}>
          <Typography variant="h6">ORIGINAL:</Typography>
          <div>
            {customerArtworkUrl ? (
              <a
                className={classes.artworkUrl}
                href={customerArtworkUrl}
                target="_blank"
                rel="noreferrer"
              >
                <img
                  className={classes.previewImage}
                  src={customerArtworkUrl}
                  alt="customerArtworkUrl"
                  style={{
                    backgroundImage:
                      "linear-gradient(45deg, #b1b1b1 25%, transparent 25%), linear-gradient(-45deg, #b1b1b1 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #b1b1b1 75%), linear-gradient(-45deg, transparent 75%, #b1b1b1 75%)",
                    backgroundSize: "20px 20px",
                    backgroundPosition: "0 0, 0 10px, 10px -10px, -10px 0px"
                  }}
                />
              </a>
            ) : (
              <Typography className={classes.missingArtworkLabel}>
                Missing artwork
              </Typography>
            )}
          </div>
        </div>
        <div className={classes.customizationAreaImageItem}>
          <Typography variant="h6">OUTPUT:</Typography>
          <div>
            {artworkUrl ? (
              <a
                className={classes.artworkUrl}
                href={artworkUrl}
                target="_blank"
                rel="noreferrer"
              >
                <img
                  className={classes.previewImage}
                  src={artworkUrl}
                  alt="artworkUrl"
                  style={{
                    backgroundImage:
                      "linear-gradient(45deg, #b1b1b1 25%, transparent 25%), linear-gradient(-45deg, #b1b1b1 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #b1b1b1 75%), linear-gradient(-45deg, transparent 75%, #b1b1b1 75%)",
                    backgroundSize: "20px 20px",
                    backgroundPosition: "0 0, 0 10px, 10px -10px, -10px 0px"
                  }}
                />
              </a>
            ) : (
              <Typography className={classes.missingArtworkLabel}>
                Missing artwork
              </Typography>
            )}
          </div>
        </div>
      </div>
      <Typography variant="subtitle1">
        {`Line Item: ${index + 1}. Quantity: ${quantity || "---"}`}{" "}
        {customerArtworkUrl && <a href={customerArtworkUrl}>(zoom)</a>}
      </Typography>
      {artNotesCollection && artNotesCollection.length > 0 && (
        <div>
          <Divider className={classes.summaryDivider} />
          <OrderNotesView artNotesCollection={artNotesCollection} />
          <Divider className={classes.summaryDivider} />
        </div>
      )}
    </div>
  );
};

export default withSnackbar(ArtReviewOrderDetails);
